import { useAuth } from "@clerk/clerk-react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SearchParams, UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isDemoUser } from "utils/demo";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAYED_ONBOARDING_PAYWALL,
} from "utils/localStorage";
import { getParams, trackEvent } from "utils/tracking";

import ActionItemList from "./ActionItemList";
import Agent from "./Agent";
import CurrentPlanCard from "./CurrentPlanCard";
import HomeVideo from "./HomeVideo";
import OnboardingStepper from "./OnboardingStepper";
import ResourcesHub from "./ResourcesHub";
import SpecialAnnouncementCard from "./SpecialAnnouncement";
import TipsFromCreators from "./TipsFromCreators";
import VirtualAssistantCard from "./VirtualAssistantCard";
import styles from "./styles";

const HomePage = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    currentOrg,
    currentUser,
    profile,
    totalEmailsSent,
    setTotalEmailsSent,
  } = useContext(OrganizationUserContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const [fetchLoading, setFetchLoading] = useState(false);

  const isDemoAccount = isDemoUser(currentOrg);
  const isFromInstagramOnboarding =
    localStorage.getItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";
  const alreadyDisplayedOnboardingPaywall =
    localStorage.getItem(
      `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";

  const upgradeDialogSource = getParams(SearchParams.UPGRADE_DIALOG_SOURCE);

  const today = new Date();

  const getTodayDate = () => {
    return today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchActionItems = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items`,
      );
      setActionItems(res.actionItems);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const checkIfEmailsSent = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/total-count`,
      );
      setTotalEmailsSent(res.count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const displayPaywallsPostOnboarding = () => {
    if (currentOrg?.isBrand) {
      return;
    }
    if (
      upgradeDialogSource &&
      Object.values(UpgradeDialogSource).includes(
        upgradeDialogSource as UpgradeDialogSource,
      )
    ) {
      setUpgradeDialogSource(upgradeDialogSource as UpgradeDialogSource);
      return;
    }
    const hasSubscribed = subscription?.isSubscribed;
    // Display usual onboarding paywall
    if (hasSubscribed) {
      return;
    }

    if (!alreadyDisplayedOnboardingPaywall) {
      setUpgradeDialogSource(UpgradeDialogSource.onboarding_paywall);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchActionItems();
      checkIfEmailsSent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      displayPaywallsPostOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    alreadyDisplayedOnboardingPaywall,
    isFromInstagramOnboarding,
  ]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentOrg?.isBrand) {
    return <Navigate to={`/${routes.inbox}`} replace />;
  }

  if (isDemoAccount) {
    return (
      <Grid container justifyContent="center">
        <Grid item xs={12} lg={discoverTab !== null ? 12 : 9}>
          <Routes>
            <Route
              path="/:agentId?"
              element={<Agent fetchLoading={fetchLoading} />}
            />
          </Routes>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ pb: 10 }} gap={1} alignItems={"flex-start"}>
      <Grid item xs={12} lg={discoverTab !== null ? 12 : 8}>
        <Box sx={styles.whiteCard}>
          <SpecialAnnouncementCard />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: 30, md: 40 },
              lineHeight: 1.3,
              maxWidth: 800,
            }}
          >
            {profile?.hasOnboarded ? (
              <>
                {currentUser?.name}, action items for you to do on{" "}
                {getTodayDate()}
              </>
            ) : (
              <>
                {currentUser?.name}, let's help you land your first brand deal
                this month
              </>
            )}
          </Typography>

          <Divider sx={{ my: 3, borderColor: "purple.light" }} />

          {profile?.hasOnboarded ? (
            <>
              {actionItems?.length > 0 ? (
                <Typography gutterBottom sx={styles.subtitle}>
                  Complete these action items to{" "}
                  <Box component="span" sx={{ color: "#00BA13" }}>
                    increase your chances
                  </Box>{" "}
                  of landing brand deals.
                </Typography>
              ) : (
                <Typography gutterBottom sx={styles.subtitle}>
                  Creators who follow these steps are{" "}
                  <Box component="span" sx={{ color: "#00BA13" }}>
                    3x more likely
                  </Box>{" "}
                  to book a paid brand collab on Bento.
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={styles.subtitle}>
              Complete these steps below to help Bento{" "}
              <Box component="span" sx={{ color: "#00BA13" }}>
                recommend the right brands
              </Box>{" "}
              for you.
            </Typography>
          )}

          {profile?.hasOnboarded ? (
            <>
              <ActionItemList fetchLoading={fetchLoading} />
            </>
          ) : (
            <Box display="flex">
              <Box sx={{ mt: 2 }}>
                <OnboardingStepper totalEmailsSent={totalEmailsSent} />
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={styles.whiteCard}>
          <TipsFromCreators />
        </Box>
      </Grid>

      <Grid container item xs lg gap={1}>
        <Grid item xs={12}>
          <CurrentPlanCard />
        </Grid>
        <Grid item xs={12}>
          <HomeVideo />
        </Grid>
        <Grid item xs={12}>
          {!currentOrg?.isVaUser && <VirtualAssistantCard />}
        </Grid>
        <ResourcesHub />
      </Grid>
    </Grid>
  );
};

export default HomePage;
